import React, { createContext } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'react-router-redux';
import shared18Next from '@xanadu/ui-shared/javascripts/i18n';
import { initialiseDisplayPreferences, setOddsType } from '@xanadu/ui-shared/modules/user/actions';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import { userInformation as gtmUserInformation } from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { initialiseUser } from '@xanadu/ui-shared/modules/userSession/actions';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { setupZendeskSettings } from '@xanadu/ui-shared/interfaces/zendesk';
import { PRODUCT_ROUTES, PRODUCTS } from '@xanadu/ui-shared/constants';
import { changeProduct } from '@xanadu/ui-shared/modules/application/actions';
import { setAccountId } from '@xanadu/ui-shared/modules/userSession/api';

import 'driver.js/dist/driver.css';
import 'hint.css/hint.min.css';

import configureStore, { history } from '../common/configureStore';

import App from '../common/components/App';
import PreLaunch from '../common/components/PreLaunch';
import NotFound from '../common/components/NotFound';
import PageWrapper from './pages/PageWrapper';
import { useIsPreLaunch } from './hooks';
import { loadAnalytics } from './utils/analytics';

import '../../stylesheets/index.scss';

export const DispatchContext = createContext(null);

// Apple Pay Integration Nuvei
const applePayScript = document.createElement('script');
applePayScript.src = 'https://cdn.safecharge.com/safecharge_resources/v1/sc_applepay.min.js';
document.head.appendChild(applePayScript);

// TrustPilot widget
const trustpilotScript = document.createElement('script');
trustpilotScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
trustpilotScript.defer = true;
trustpilotScript.async = true;
document.body.appendChild(trustpilotScript);

loadAnalytics(__ANALYTICS_SITE_ID__);

const bootApp = ({
  routes = [],
  reducers,
  sagas,
  site,
  brand,
  domain,
  ZENDESK_KEY,
  ZENDESK_WIDGET_TYPE,
  clientDefaultLanguage,
  clientDefaultCurrency,
  availableCurrencies,
  modalContents,
  fallbackReleaseDate,
  defaultProduct = PRODUCTS.exchange,
  ...configProps // capturing anything else coming from config
}) => async () => {
  setupZendeskSettings();

  const {
    liveChatEnabled,
    fixedOddsType,
  } = configProps.featuresFlags;

  let language = clientDefaultLanguage || 'en-GB';

  /**
    We must to this because the navigator.language is always
    populated but has a lot of languages that we do not support
  */
  if (configProps.availableLanguages.includes(window.navigator.language)) {
    language = window.navigator.language;
  }

  try {
    language = localStorage.getItem(`${brand}-ui-language`) || language;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Couldn\'t get user storage language', e);
  }

  const {
    // fetching,
    isPreLaunch,
    isAppPreLaunch,
    androidStoreLink = null,
    iosStoreLink = null,
    enablePreLaunch,
    preLaunchText,
    underageWarning,
    preLaunchImage,
  } = await useIsPreLaunch({
    brand,
    site,
    domain,
    path: history.location.pathname,
    fallbackReleaseDate,
  });

  let store = null;

  const isPreLaunchPath = history.location.pathname.startsWith('/pre-launch');

  /** The blocking operation below is necessary to dynamically populate and import components from config.js.
   * Another option is to pass the PATH down to a HOC route, let it import the component while showing a placeholder.
   *
   * Consider the operation below takes between 40ms and 70ms to complete,
   * if it would be asyncronous, there wouldn't be a blocking,
   * though we would need to wait the loader at route/component level
   */
  // const computedRoutes = await importDynamicRouteComponents(routes);

  shared18Next.initLocales(language, configProps.availableLanguages);

  const bootStore = () => {
    (() => {
      // User returning times flag
      try {
        const userReturningTimes = localStorage.getItem('mb-user-returning-times');

        if (!userReturningTimes) {
          localStorage.setItem('mb-user-returning-times', '1');
        } else {
          const update = `${parseInt(userReturningTimes, 10) + 1}`;
          localStorage.setItem('mb-user-returning-times', update);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Couldn\'t set user returning times flag', e);
      }
    })();

    store = configureStore({
      site,
      brand,
      domain,
      language,
      defaultProduct,
      availableFeatures: configProps.availableFeatures,
      availableCurrencies,
      clientDefaultCurrency,
      isPreLaunch: enablePreLaunch,
      settings: configProps.settings,
      featuresFlags: configProps.featuresFlags,
      customization: configProps.customization,
      excludedSports: configProps.excludedSports,
      liveChatEnabled,
      ZENDESK_WIDGET_TYPE,
      ZENDESK_KEY,
      // Here to add brand specific reducers and sagas
      reducers,
      sagas,
    });

    return store;
  };

  const initialise = (userData) => {
    store.dispatch(initialiseDisplayPreferences(userData));

    if (userData.username) {
      store.dispatch(initialiseUser(userData));
    } else {
      setAccountId();

      if (!fixedOddsType) {
        store.dispatch(setOddsType(userData['odds-type']));
      }
    }

    const { subProductsEnabled = [] } = configProps.featuresFlags;

    let storedProduct = defaultProduct;

    // Build product paths string for the product routes
    const productKeys = Object.keys(PRODUCT_ROUTES)
      .filter(key => ![PRODUCTS.exchange, PRODUCTS.futures, PRODUCTS.predictions].includes(key)
        && subProductsEnabled.includes(key));

    try {
      window.userReturning = localStorage.getItem(`${brand}-user-returning`);

      localStorage.setItem(`${brand}-user-returning`, true);

      // change this to be brand/site specific, first need to updae ui-shared sagas usage
      storedProduct = localStorage.getItem('mb-product');

      const isValidProduct = productKeys.some(key => key === storedProduct);

      if (!isValidProduct) {
        storedProduct = defaultProduct;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing localStorage. Most likely due to a form of incognito mode', e);
    }

    store.dispatch(changeProduct(storedProduct));

    gtmDispatch(gtmUserInformation(userData));

    window.bootTimestamp = Date.now();

    render(
      <Provider store={store}>
        <DispatchContext.Provider value={store.dispatch}>
          <ConnectedRouter history={history}>
            <ChildrenPropsContext.Provider value={{ ...configProps }}>
              <App
                isPreLaunch={enablePreLaunch}
                modalContents={modalContents}
                configProps={{ ...configProps }}
              >
                {isPreLaunch && !isPreLaunchPath && <Redirect from="*" to="/pre-launch" />}
                <Switch>
                  {routes.map(({
                    path,
                    exact,
                    component: Component,
                    props,
                    seo = true,
                  }) => (
                    <Route
                      key={path}
                      path={path}
                      exact={exact}
                      component={(routeProps) => (
                        <PageWrapper
                          Component={Component}
                          componentProps={{ ...props, ...routeProps }}
                          configProps={configProps}
                          brand={brand}
                          site={site}
                          seo={seo}
                        />
                      )}
                    />
                  ))}
                  <Route
                    path="/pre-launch"
                    component={(routeProps) => (
                      <PageWrapper
                        Component={PreLaunch}
                        componentProps={{
                          ...routeProps,
                          isAppPreLaunch,
                          androidStoreLink,
                          iosStoreLink,
                          preLaunchText,
                          underageWarning,
                          fallbackReleaseDate,
                          image: preLaunchImage,
                        }}
                        configProps={configProps}
                        brand={brand}
                        site={site}
                        seo={enablePreLaunch}
                      />
                    )}
                  />
                  <Route component={NotFound} />
                </Switch>
              </App>
            </ChildrenPropsContext.Provider>
          </ConnectedRouter>
        </DispatchContext.Provider>
      </Provider>,
      document.getElementById('root')
    );
  };

  return {
    initialise,
    bootStore,
  };
};

export default bootApp;
